import { Grid } from '../../core/Grid.js';
import {
    Position,
    positionKey,
    parsePositionKey,
    edgeKey,
} from '../../core/GridGeometry.js';
import { BranchesGame } from './BranchesGame.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class BranchesVortex extends BranchesGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // -- Vortex Data -------------------------------------------------------

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.computeState();
        });
    }

    // -- Gate Queries ------------------------------------------------------

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, () => {
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.computeState();
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // -- Branching Validation with Merged Gate Pairs -----------------------

    protected override validateBranching(adjacency: Map<string, string[]>): void {
        // The stem's rim-link counts as a connection at its entry cell (see BranchesGame).
        const stemCell = this.getStemEntryCell();
        const stemKey = stemCell ? positionKey(stemCell) : null;

        // Check non-gate cells normally
        for (const [cellKey, neighbors] of adjacency) {
            const degree = neighbors.length + (cellKey === stemKey ? 1 : 0);
            if (degree > 2) {
                const pos = parsePositionKey(cellKey);
                if (!this.grid.hasDigitAt(pos) && !this.gm.isGateCellByKey(cellKey)) {
                    this.branchingViolations.add(cellKey);
                }
            }
        }

        // Check active gate pairs: at most 1 connection on each side
        const checked = new Set<string>();
        for (const [keyA, keyB] of this.gm.raw) {
            if (checked.has(keyA)) continue;
            checked.add(keyA);
            checked.add(keyB);

            const connectionsA = (adjacency.get(keyA) || []).length;
            const connectionsB = (adjacency.get(keyB) || []).length;
            if (connectionsA > 1 || connectionsB > 1) {
                if (connectionsA > 1) this.branchingViolations.add(keyA);
                if (connectionsB > 1) this.branchingViolations.add(keyB);
            }
        }

        // Check inactive gate cells individually (they're just empty cells)
        for (const gateKey of this.gm.getAllGatePositions()) {
            if (this.gm.raw.has(gateKey)) continue; // Handled above as pair
            const neighbors = adjacency.get(gateKey);
            if (neighbors && neighbors.length > 2) {
                this.branchingViolations.add(gateKey);
            }
        }
    }

    // -- Endpoints: exclude active gate cells --------------------------------

    override getEndpoints(): Array<[Position, number]> {
        // Active gate cells with 1 physical segment are not real tips —
        // they connect through to the paired gate at the same distance.
        return super.getEndpoints().filter(
            ([pos]) => !this.gm.raw.has(positionKey(pos))
        );
    }

    // -- Compute State with Gate Pair Merging ------------------------------

    protected override computeState(): void {
        this.connectedSegments.clear();
        this.disconnectedSegments.clear();
        this.distances.clear();
        this.branchingViolations.clear();

        const adjacency = this.buildAdjacencyGraph();
        this.validateBranching(adjacency);

        // If no stem, all segments are disconnected
        if (!this.stem) {
            for (const seg of this.segments) {
                this.disconnectedSegments.add(seg);
            }
            return;
        }

        const stemCell = this.getStemEntryCell()!;
        const stemKey = positionKey(stemCell);

        // BFS from stem cell with gate pair merging
        const visited = new Set<string>();
        const queue: Array<{ key: string; distance: number }> = [];

        if (adjacency.has(stemKey) || this.gm.raw.has(stemKey)) {
            queue.push({ key: stemKey, distance: 0 });
            this.distances.set(stemKey, 0);

            // If stem entry is an active gate, also enqueue paired cell at distance 0
            if (this.gm.raw.has(stemKey)) {
                const pairedKey = this.gm.raw.get(stemKey)!;
                if (!this.distances.has(pairedKey)) {
                    this.distances.set(pairedKey, 0);
                    queue.push({ key: pairedKey, distance: 0 });
                }
            }
        }

        while (queue.length > 0) {
            const { key, distance } = queue.shift()!;
            if (visited.has(key)) continue;
            visited.add(key);

            // If this is an active gate, also visit the paired cell at same distance
            if (this.gm.raw.has(key)) {
                const pairedKey = this.gm.raw.get(key)!;
                if (!visited.has(pairedKey)) {
                    if (!this.distances.has(pairedKey)) {
                        this.distances.set(pairedKey, distance);
                    }
                    queue.push({ key: pairedKey, distance });
                }
            }

            const neighbors = adjacency.get(key) || [];
            for (const neighborKey of neighbors) {
                // Mark the segment as connected
                const pos = parsePositionKey(key);
                const neighborPos = parsePositionKey(neighborKey);
                const edge = this.gg.createEdgeFromCells(pos, neighborPos);
                if (edge) {
                    this.connectedSegments.add(edgeKey(edge));
                }

                if (!visited.has(neighborKey)) {
                    const newDist = distance + 1;
                    if (!this.distances.has(neighborKey)) {
                        this.distances.set(neighborKey, newDist);
                        queue.push({ key: neighborKey, distance: newDist });
                    }
                }
            }
        }

        // All segments not connected are disconnected
        for (const seg of this.segments) {
            if (!this.connectedSegments.has(seg)) {
                this.disconnectedSegments.add(seg);
            }
        }
    }

    // -- Status ------------------------------------------------------------

    override getStatus(): string {
        const base = super.getStatus();

        // Replace generic branching message with gate-specific one
        if (this.branchingViolations.size > 0) {
            const hasGateViolation = [...this.branchingViolations].some(
                key => this.gm.raw.has(key)
            );
            if (hasGateViolation) {
                return 'Branches must traverse gates, not branch at them.';
            }
        }

        return base;
    }

    // -- State Management --------------------------------------------------

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // -- Serialization -----------------------------------------------------

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();

        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.computeState();
    }
}
